/**
 * WebRTC getStats() extraction shared by the 1:1 screen share and meetings.
 * Produces a compact snapshot answering "what did the connection look like
 * right before it dropped": selected candidate pair (host/srflx/relay tells
 * the NAT story), RTT trend, packet loss, encoder quality limitation.
 */

export interface RtcStatsSnapshot {
  pair?: {
    local_type?: string;
    remote_type?: string;
    protocol?: string;
    rtt?: number;
    available_outgoing_bitrate?: number;
  };
  inbound_video?: {
    packets_lost?: number;
    jitter?: number;
    bytes?: number;
    fps?: number;
  };
  outbound_video?: {
    bytes?: number;
    fps?: number;
    quality_limitation?: string;
  };
  inbound_audio?: {
    packets_lost?: number;
    jitter?: number;
    /** packetsReceived — 0 vs missing distinguishes "no RTP" from "no stats". */
    packets?: number;
    bytes?: number;
    /** Instantaneous decoded level [0..1] — nonzero = actual sound arrived. */
    audio_level?: number;
    /** Cumulative energy — distinguishes "13 s of speech" from digital silence. */
    total_energy?: number;
  };
  /** Whether our mic audio is actually leaving the machine — the 2026-08-04
   *  silent meeting was invisible in diagnostics precisely for lack of this. */
  outbound_audio?: {
    bytes?: number;
    packets?: number;
  };
  /** Local mic CAPTURE level (media-source) — zero while packets flow means
   *  the mic is muted at the OS/hardware level and we are sending silence. */
  mic?: {
    audio_level?: number;
    total_energy?: number;
  };
}

/** "candidate:… typ srflx …" → "srflx". */
export function parseCandidateType(candidate?: string | null): string {
  if (!candidate) return "unknown";
  const match = / typ (\w+)/.exec(candidate);
  return match?.[1] ?? "unknown";
}

interface AnyStats {
  id: string;
  type: string;
  [key: string]: unknown;
}

export async function collectStatsSnapshot(
  pc: RTCPeerConnection,
): Promise<RtcStatsSnapshot | null> {
  let report: RTCStatsReport;
  try {
    report = await pc.getStats();
  } catch {
    return null;
  }

  const byId = new Map<string, AnyStats>();
  report.forEach((s) => byId.set((s as AnyStats).id, s as AnyStats));

  const snapshot: RtcStatsSnapshot = {};

  let pair: AnyStats | undefined;
  byId.forEach((s) => {
    if (s.type === "transport" && typeof s.selectedCandidatePairId === "string") {
      pair = byId.get(s.selectedCandidatePairId) ?? pair;
    }
  });
  if (!pair) {
    // Firefox: no transport.selectedCandidatePairId — fall back to the
    // succeeded/selected candidate-pair entry.
    byId.forEach((s) => {
      if (
        s.type === "candidate-pair" &&
        (s.selected === true || s.nominated === true) &&
        s.state === "succeeded"
      ) {
        pair = pair ?? s;
      }
    });
  }
  if (pair) {
    const local = byId.get(String(pair.localCandidateId ?? ""));
    const remote = byId.get(String(pair.remoteCandidateId ?? ""));
    snapshot.pair = {
      local_type: local?.candidateType as string | undefined,
      remote_type: remote?.candidateType as string | undefined,
      protocol: (local?.protocol ?? pair.protocol) as string | undefined,
      rtt: pair.currentRoundTripTime as number | undefined,
      available_outgoing_bitrate: pair.availableOutgoingBitrate as
        | number
        | undefined,
    };
  }

  byId.forEach((s) => {
    if (s.type === "inbound-rtp" && s.kind === "video") {
      snapshot.inbound_video = {
        packets_lost: s.packetsLost as number | undefined,
        jitter: s.jitter as number | undefined,
        bytes: s.bytesReceived as number | undefined,
        fps: s.framesPerSecond as number | undefined,
      };
    } else if (s.type === "inbound-rtp" && s.kind === "audio") {
      snapshot.inbound_audio = {
        packets_lost: s.packetsLost as number | undefined,
        jitter: s.jitter as number | undefined,
        packets: s.packetsReceived as number | undefined,
        bytes: s.bytesReceived as number | undefined,
        audio_level: s.audioLevel as number | undefined,
        total_energy: s.totalAudioEnergy as number | undefined,
      };
    } else if (s.type === "media-source" && s.kind === "audio") {
      snapshot.mic = {
        audio_level: s.audioLevel as number | undefined,
        total_energy: s.totalAudioEnergy as number | undefined,
      };
    } else if (s.type === "outbound-rtp" && s.kind === "audio") {
      snapshot.outbound_audio = {
        bytes: s.bytesSent as number | undefined,
        packets: s.packetsSent as number | undefined,
      };
    } else if (s.type === "outbound-rtp" && s.kind === "video") {
      snapshot.outbound_video = {
        bytes: s.bytesSent as number | undefined,
        fps: s.framesPerSecond as number | undefined,
        quality_limitation: s.qualityLimitationReason as string | undefined,
      };
    }
  });

  return snapshot;
}
